import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Mic, MessagesSquare, Inbox, Sparkles } from "lucide-react";
import { SubPage, EmptyState, Spinner } from "../components/common";
import { api, fileUrl, mediaUrl } from "../lib/api";
import { useLang } from "../lib/i18n";
import { chatTime, mmss } from "../lib/chat";
import { useUnread } from "../context/UnreadContext";

export function Avatar({ user, size = "h-12 w-12" }) {
  return (
    <div className={`${size} shrink-0 overflow-hidden rounded-full bg-brand/15`}>
      {user?.avatar_url ? <img src={mediaUrl(user.avatar_url)} alt="" className="h-full w-full object-cover" referrerPolicy="no-referrer" />
        : <div className="flex h-full w-full items-center justify-center text-lg font-extrabold text-navy">{user?.name?.[0]?.toUpperCase() || "?"}</div>}
    </div>
  );
}

export function Preview({ msg, mine }) {
  const { t } = useLang();
  if (!msg) return null;
  const you = mine ? t("msg.you") : "";
  if (msg.type === "match") return <span className="font-semibold text-alert">{t("contact.matchTitle")}</span>;
  if (msg.type === "request") return <span>{mine ? t("contact.pending") : t("contact.requestPreview")}</span>;
  if (msg.type === "audio") return <span className="inline-flex items-center gap-1">{you}<Mic className="h-3.5 w-3.5 text-brand" /> {t("msg.audio", { d: mmss(msg.duration) })}</span>;
  return <span>{you}{msg.text}</span>;
}

const TABS = ["chats", "requests", "matches"];

function Row({ c, isRequest, onOpen }) {
  const { t, lang } = useLang();
  const name = c.other.deleted ? t("msg.deletedUser") : c.other.name;
  return (
    <button onClick={onOpen} data-testid={`conversation-row-${c.id}`}
      className="flex w-full items-center gap-3 rounded-2xl bg-white px-3.5 py-3 text-left shadow-[0_2px_12px_rgba(13,42,107,0.06)] transition-[transform,box-shadow] duration-200 hover:shadow-[0_6px_18px_rgba(13,42,107,0.1)] active:scale-[0.99]">
      <div className="relative">
        <Avatar user={c.other} />
        {c.listing.photo && <img src={fileUrl(c.listing.photo)} alt="" className="absolute -bottom-1 -right-1 h-6 w-6 rounded-md border-2 border-white object-cover" />}
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-baseline justify-between gap-2">
          <p className={`truncate text-[15px] text-navy ${c.unread ? "font-extrabold" : "font-bold"}`} data-testid="conversation-row-name">
            {isRequest ? t("msg.wantsToContact", { name }) : name}
          </p>
          <span className="shrink-0 text-[11px] text-slate-400">{chatTime(c.last_message_at, lang)}</span>
        </div>
        <div className="flex items-center gap-1.5">
          <p className="min-w-0 truncate text-xs font-semibold text-brand" data-testid="conversation-row-listing">{c.listing.title}</p>
          {c.matched && (
            <span className="inline-flex shrink-0 items-center gap-0.5 rounded-full bg-alert/10 px-1.5 py-0.5 text-[10px] font-bold text-alert" data-testid="conversation-row-match-tag">
              <Sparkles className="h-3 w-3" /> {t("contact.matchTag")}
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <p className={`min-w-0 flex-1 truncate text-sm ${c.unread ? "font-semibold text-navy" : "text-slate-500"}`} data-testid="conversation-row-preview">
            {c.status === "pending" && c.role === "buyer" ? t("contact.pending") : <Preview msg={c.last_message} mine={c.last_message?.sender_id !== c.other.user_id} />}
          </p>
          {c.unread > 0 && <span className="h-2.5 w-2.5 shrink-0 rounded-full bg-alert" data-testid="conversation-row-unread" />}
        </div>
      </div>
    </button>
  );
}

export default function MessagesPage() {
  const { t } = useLang();
  const navigate = useNavigate();
  const { refresh } = useUnread();
  const [params, setParams] = useSearchParams();
  const [data, setData] = useState(null);
  const tab = TABS.includes(params.get("tab")) ? params.get("tab") : "chats";
  const setTab = (k) => setParams({ tab: k }, { replace: true });

  useEffect(() => {
    const load = () => api.get("/conversations").then((r) => setData(r.data)).catch(() => {});
    load();
    refresh();
    const id = setInterval(load, 15000);
    return () => clearInterval(id);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (tab === "matches") api.post("/matches/seen").then(refresh).catch(() => {});
  }, [tab]); // eslint-disable-line react-hooks/exhaustive-deps

  const list = data?.[tab] || [];
  const icons = { chats: MessagesSquare, requests: Inbox, matches: Sparkles };
  return (
    <SubPage title={t("messages.title")} testid="messages-page">
      <div className="mb-4 grid grid-cols-3 gap-1 rounded-full bg-white p-1 shadow-[0_2px_12px_rgba(13,42,107,0.06)]" data-no-swipe>
        {TABS.map((k) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`messages-tab-${k}`}
            className={`rounded-full py-2.5 text-[13px] font-bold transition-colors duration-200 ${tab === k ? "bg-brand text-white" : "text-navy hover:bg-mist"}`}>
            {t(`msg.${k}`)}{k === "requests" && data ? ` (${data.requests.length})` : ""}
          </button>
        ))}
      </div>
      {!data ? <div className="flex justify-center py-10"><Spinner /></div> : list.length === 0 ? (
        <EmptyState icon={icons[tab]} testid={`messages-empty-${tab}`}
          title={t(`msg.empty${tab[0].toUpperCase()}${tab.slice(1)}`)}
          sub={t(`msg.empty${tab[0].toUpperCase()}${tab.slice(1)}Sub`)} />
      ) : (
        <div className="space-y-2.5" data-testid={`messages-list-${tab}`}>
          {list.map((c) => <Row key={c.id} c={c} isRequest={tab === "requests"} onOpen={() => navigate(`/mensajes/${c.id}`)} />)}
        </div>
      )}
    </SubPage>
  );
}
